
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { UserProfile } from '../types';
import { Loader2, Sparkles, Download, Check, RefreshCw, ChevronLeft, ShieldCheck, Clock } from 'lucide-react';

interface StylePreset {
  id: string;
  name: string;
  description: string;
  previewColor: string;
  icon: string;
}

const STYLE_PRESETS: StylePreset[] = [
  { id: 'gta', name: 'GTA Loading Screen', description: 'Estilo Grand Theft Auto V, vibrante e marcante.', previewColor: 'from-orange-500 to-yellow-500', icon: '🚗' },
  { id: 'pixar', name: 'Disney Pixar', description: '3D fofo e carismático, estilo animação moderna.', previewColor: 'from-blue-400 to-indigo-500', icon: '🎬' },
  { id: 'cyberpunk', name: 'Cyberpunk 2077', description: 'Luzes neon, futuro distópico e alta tecnologia.', previewColor: 'from-purple-600 to-pink-500', icon: '🧬' },
  { id: 'anime', name: 'Studio Ghibli', description: 'Estilo anime clássico, traços suaves e artísticos.', previewColor: 'from-green-400 to-teal-500', icon: '🗾' },
  { id: 'professional', name: 'Executivo', description: 'Foto profissional, iluminação de estúdio e foco nítido.', previewColor: 'from-slate-600 to-slate-800', icon: '💼' },
  { id: 'lego', name: 'Lego Hero', description: 'Seu avatar como um bonequinho da Lego.', previewColor: 'from-yellow-400 to-red-500', icon: '🧱' },
  { id: 'caricature', name: 'Caricatura Digital', description: 'Estilo divertido com traços exagerados e coloridos.', previewColor: 'from-pink-400 to-orange-400', icon: '🎨' }
];

interface AIAvatarGeneratorProps {
  userProfile: UserProfile | null;
  onBack: () => void;
  onUpdateProfile: (newAvatarUrl: string) => void;
}

export const AIAvatarGenerator: React.FC<AIAvatarGeneratorProps> = ({ userProfile, onBack, onUpdateProfile }) => {
  const [selectedStyle, setSelectedStyle] = useState<string>('gta');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [cooldownDays, setCooldownDays] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkCooldown();
  }, [userProfile]);

  const checkCooldown = () => {
    if (userProfile?.last_avatar_gen_at) {
      const lastGen = new Date(userProfile.last_avatar_gen_at);
      const now = new Date();
      const diffInMs = now.getTime() - lastGen.getTime();
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
      
      if (diffInDays < 15) {
        setCooldownDays(Math.ceil(15 - diffInDays));
      } else {
        setCooldownDays(null);
      }
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const { data, error } = await supabase.functions.invoke('generate-avatar', {
        body: { style: selectedStyle }
      });

      if (error) {
          if (error.message && error.message.includes('COOLDOWN_ACTIVE')) {
              setError("Você ainda está no período de espera. Tente novamente em alguns dias.");
              return;
          }
          throw error;
      }

      setGeneratedImage(`data:image/png;base64,${data.imageBase64}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Erro ao gerar imagem. Verifique sua conexão ou tente mais tarde.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedImage) return;
    setIsSaving(true);
    setError(null);

    try {
      const base64 = generatedImage.split(',')[1];
      const { data, error } = await supabase.functions.invoke('save-avatar', {
        body: { imageBase64: base64 }
      });

      if (error) throw error;

      onUpdateProfile(data.avatarUrl);
      alert("Avatar atualizado com sucesso! 🚀");
      onBack();
    } catch (err: any) {
      console.error(err);
      setError("Erro ao salvar avatar.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `driver-score-avatar-${selectedStyle}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-slate-800 rounded-full transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="text-center flex-1 pr-10">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
            Gerador de Avatar IA
          </h1>
          <p className="text-slate-400 text-sm">Exclusivo para Membros VIP & Premium</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Left Column: Selector */}
        <div className="space-y-6">
          <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-3xl backdrop-blur-sm">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Escolha seu Estilo
            </h2>
            
            <div className="grid grid-cols-1 gap-3">
              {STYLE_PRESETS.map((style) => (
                <button
                  key={style.id}
                  onClick={() => {
                    if (!isGenerating && !generatedImage) setSelectedStyle(style.id);
                  }}
                  disabled={isGenerating || !!generatedImage}
                  className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                    selectedStyle === style.id 
                      ? 'bg-slate-700 border-orange-500 shadow-lg shadow-orange-500/10' 
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 grayscale hover:grayscale-0 opacity-70 hover:opacity-100'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${style.previewColor} flex items-center justify-center text-2xl shadow-inner`}>
                    {style.icon}
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-slate-100">{style.name}</div>
                    <div className="text-xs text-slate-400 leading-tight">{style.description}</div>
                  </div>
                  {selectedStyle === style.id && (
                    <div className="ml-auto bg-orange-500 rounded-full p-1">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="mt-8">
              {cooldownDays ? (
                <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-2xl flex items-center gap-3">
                  <div className="bg-orange-500/20 p-2 rounded-full">
                    <Clock className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-orange-400">Aguarde o Cooldown</div>
                    <div className="text-slate-400 text-xs">Você poderá gerar uma nova imagem em **{cooldownDays} dias**.</div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !!generatedImage}
                  className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${
                    isGenerating || !!generatedImage
                      ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-lg shadow-orange-600/20 hover:scale-[1.02] active:scale-95'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Gerando Magia...
                    </>
                  ) : generatedImage ? (
                    <>
                      <Check className="w-5 h-5" />
                      Imagem Pronta!
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Gerar Avatar Grátis
                    </>
                  )}
                </button>
              )}
              <p className="text-[10px] text-center text-slate-500 mt-3">
                * A IA usará sua foto de perfil atual como base.<br/>
                * Limite de 1 geração a cada 15 dias para membros Premium.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column: Result */}
        <div className="flex flex-col gap-6">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-3xl aspect-square flex items-center justify-center relative overflow-hidden group shadow-2xl">
            {isGenerating ? (
              <div className="flex flex-col items-center gap-4 animate-pulse">
                <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-orange-500" />
                </div>
                <div className="text-slate-400 font-medium">A IA está pintando seu avatar...</div>
              </div>
            ) : generatedImage ? (
              <img 
                src={generatedImage} 
                alt="Generated Avatar" 
                className="w-full h-full object-cover animate-in zoom-in-95 duration-700"
              />
            ) : (
              <div className="flex flex-col items-center gap-4 text-slate-500 p-8 text-center">
                <div className="w-20 h-20 rounded-3xl bg-slate-800 flex items-center justify-center border border-slate-700">
                  <ShieldCheck className="w-8 h-8 opacity-20" />
                </div>
                <div>
                  <div className="font-semibold text-slate-400">Sua prévia aparecerá aqui</div>
                  <p className="text-xs max-w-[200px] mt-1">Selecione um estilo ao lado e clique em gerar.</p>
                </div>
              </div>
            )}
            
            {/* Overlay Gradient */}
            {generatedImage && (
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            )}
          </div>

          {generatedImage && (
            <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-4 duration-500">
              <button
                onClick={handleDownload}
                className="bg-slate-800 hover:bg-slate-700 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 border border-slate-700 transition-all active:scale-95"
              >
                <Download className="w-5 h-5" />
                Baixar
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-orange-600/20 transition-all active:scale-95 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                Usar no Perfil
              </button>
            </div>
          )}

          {generatedImage && (
            <button
              onClick={() => {
                setGeneratedImage(null);
                setError(null);
              }}
              className="text-slate-500 hover:text-slate-300 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar outro estilo
            </button>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl text-red-400 text-sm text-center">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Safety Notice */}
      <div className="mt-12 p-6 bg-blue-500/5 border border-blue-500/10 rounded-3xl">
        <h3 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          Segurança & Privacidade
        </h3>
        <p className="text-slate-400 text-xs leading-relaxed">
          Nossa IA utiliza o Google Imagen 3 para gerar as imagens de forma segura. 
          As imagens geradas não contêm dados pessoais identificáveis. 
          Ao clicar em "Usar no Perfil", a imagem anterior será substituída e a nova ficará salva em nossos servidores.
          O cooldown de 15 dias existe para garantir que todos os usuários VIP tenham acesso rápido e de qualidade ao serviço.
        </p>
      </div>
    </div>
  );
};
